import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { 
  UserIcon,
  LockClosedIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const TeacherLogin = () => {
  const [accessCode, setAccessCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    setAccessCode(e.target.value);
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Call the serverless function for secure authentication
      const response = await fetch('/api/verify-access-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ accessCode }),
      });

      const data = await response.json();

      if (data.success) {
        // Open the faculty portal in a new tab
        window.open(data.redirectUrl, '_blank');
        setAccessCode('');
        setError('');
      } else {
        setError(data.error || 'Invalid access code. Please check your code and try again.');
      }
    } catch (error) {
      console.error('Authentication error:', error);
      setError('Unable to verify access code. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main id="main-content" className="min-h-screen bg-gray-50">
      <Navbar />
      
      <section className="pt-32 pb-24 bg-[#630503] relative overflow-hidden">
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-1/5 bg-gradient-to-b from-[#630503] to-white"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="mb-16">
              <span className="inline-block bg-white/10 backdrop-blur-sm text-white px-6 py-3 text-sm font-medium uppercase tracking-[0.2em] mb-8 rounded-full">
                Faculty Access
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-light text-white mb-8 leading-[1.1] max-w-4xl mx-auto">
                Teacher <span className="font-bold">Access</span>
              </h1>
              <div className="w-24 h-px bg-white/40 mx-auto mb-8"></div>
              <p className="text-lg lg:text-xl leading-relaxed text-white/95 max-w-3xl mx-auto">
                Enter your access code to view exclusive faculty resources, collaborative discussions, and professional development materials.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
            <div className="bg-[#4a0402] px-10 py-8 text-center relative">
              <div className="absolute inset-0 bg-black/10"></div>
              <div className="relative z-10">
                <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <UserIcon className="h-10 w-10 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-white mb-2">Faculty Portal</h2>
                <p className="text-white/90 text-lg">Access Code Required</p>
              </div>
            </div>

            <div className="px-10 py-10">
              {error && (
                <div className="mb-8 bg-red-50 border-l-4 border-red-400 rounded-lg p-5 flex items-start">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-500 mt-0.5 mr-4 flex-shrink-0" />
                  <div>
                    <p className="text-base text-red-800 font-semibold">Authentication Failed</p>
                    <p className="text-sm text-red-700 mt-1">{error}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="space-y-2">
                  <label htmlFor="accessCode" className="block text-base font-semibold text-gray-800">
                    Access Code
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <LockClosedIcon className="h-6 w-6 text-gray-400 group-focus-within:text-[#630503] transition-colors" />
                    </div>
                    <input
                      type="password"
                      id="accessCode"
                      name="accessCode"
                      value={accessCode}
                      onChange={handleInputChange}
                      className="block w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl text-lg bg-gray-50/50 placeholder-gray-400 focus:outline-none focus:ring-0 focus:border-[#630503] focus:bg-white transition-all duration-200"
                      placeholder="Enter faculty access code"
                      required
                    />
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    Contact the school administration if you need the access code.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center items-center py-4 px-6 border border-transparent rounded-xl shadow-lg text-lg font-bold text-white bg-[#4a0402] hover:bg-[#630503] focus:outline-none focus:ring-4 focus:ring-[#630503]/30 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] transition-all duration-200"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-4 h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Authenticating...
                    </>
                  ) : (
                    <>
                      <span>Access Faculty Portal</span>
                      <svg className="ml-3 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default TeacherLogin; 